import { Block, CellSpec, generatedAt, INK, PdfDoc, TableSpec, text } from "./pdfDoc";

/**
 * How the other report PDFs look (Scorecard, Retention, Engagement): the daily
 * report's fonts and blues, with a title block, a paragraph saying what the
 * report shows, and a description under each number of what it means.
 */

export const HOUSE_MARGIN = 54;
/** Letter portrait between the margins. */
export const HOUSE_WIDTH = 612 - 2 * HOUSE_MARGIN;

export interface HouseOptions {
  preparedBy: string;
  /** Print what each number means under it. On by default; the card asked for it. */
  includeMeanings: boolean;
}

const body = text("helvetica", "normal", 10, [0x33, 0x33, 0x33]);
const muted = text("helvetica", "normal", 9, INK.muted);

export function titleBlock(title: string, lines: string[], preparedBy: string): Block[] {
  const blocks: Block[] = [
    { kind: "text", text: `I Love Black People: ${title}`, style: text("helvetica", "bold", 16, INK.black), x: HOUSE_MARGIN, gapBefore: 0, lineHeight: 20, maxWidth: HOUSE_WIDTH },
  ];
  const meta = [...lines, ...(preparedBy.trim() ? [`Prepared by: ${preparedBy.trim()}`] : [])];
  meta.forEach((line, index) =>
    blocks.push({ kind: "text", text: line, style: text("helvetica", "bold", 10, INK.black), x: HOUSE_MARGIN, gapBefore: index === 0 ? 4 : 0, lineHeight: 13, maxWidth: HOUSE_WIDTH }),
  );
  return blocks;
}

export const heading = (value: string, gapBefore = 22): Block => ({
  kind: "text",
  text: value,
  style: text("times", "bold", 13, INK.brightBlue),
  x: HOUSE_MARGIN,
  gapBefore,
  lineHeight: 16,
  maxWidth: HOUSE_WIDTH,
});

export const paragraph = (value: string, gapBefore = 6): Block => ({
  kind: "text",
  text: value,
  style: body,
  x: HOUSE_MARGIN,
  gapBefore,
  lineHeight: 13,
  maxWidth: HOUSE_WIDTH,
});

export const note = (value: string, gapBefore = 4): Block => ({
  kind: "text",
  text: value,
  style: muted,
  x: HOUSE_MARGIN,
  gapBefore,
  lineHeight: 11.5,
  maxWidth: HOUSE_WIDTH,
});

/** The funnel table's look: a blue header in white, light blue rules. */
export function houseTable(widths: number[], head: CellSpec[], rows: CellSpec[][], fontSize = 10): TableSpec {
  return {
    x: HOUSE_MARGIN,
    widths,
    head: [head],
    body: rows,
    text: text("times", "normal", fontSize, [0x26, 0x26, 0x26]),
    headText: { style: "bold", color: INK.white },
    headFill: INK.blue,
    lineColor: INK.rule,
    lineWidth: 0.75,
    padding: { top: 4, right: 5, bottom: 4, left: 5 },
    valign: "top",
    showHead: "everyPage",
  };
}

export const table = (spec: TableSpec, gapBefore = 8): Block => ({ kind: "table", table: spec, gapBefore });

/**
 * A row followed by what it means: the description spans the table under it,
 * with no rule between the two, so they read as one entry.
 */
export function withMeaning(cells: CellSpec[], meaning: string | null): CellSpec[][] {
  if (!meaning) return [cells];
  return [
    cells.map((cell) => ({ ...cell, rules: { ...cell.rules, bottom: 0 } })),
    [
      {
        text: meaning,
        colSpan: cells.reduce((sum, cell) => sum + (cell.colSpan ?? 1), 0),
        style: { font: "helvetica", size: 8, color: INK.muted },
        padding: { top: 0, bottom: 6 },
        rules: { top: 0 },
      },
    ],
  ];
}

export function houseDoc(fileName: string, blocks: Block[], footer: string, orientation: PdfDoc["orientation"] = "portrait"): PdfDoc {
  return {
    fileName,
    orientation,
    margin: { top: HOUSE_MARGIN, right: HOUSE_MARGIN, bottom: HOUSE_MARGIN, left: HOUSE_MARGIN },
    blocks,
    footer: `${footer} · Generated ${generatedAt()}`,
  };
}
